import {
	storageDefaultKey,
	storageUserTokenKey,
	STARMANIA_AUTH_TOKEN_STORAGE_KEY,
	storageImpersonateKey,
	SUPERBUS_AUTH_TOKEN_STORAGE_KEY,
} from '@constants/storageKeys';

const localStorage = typeof window !== 'undefined' ? window.localStorage : null;

export const cleanDeprecatedStorageKey = (): void => {
	if (!localStorage) return;
	localStorage.removeItem(storageUserTokenKey);
};

export const cleanJWTStorageKey = (): void => {
	if (!localStorage) return;
	localStorage.removeItem(STARMANIA_AUTH_TOKEN_STORAGE_KEY);
	localStorage.removeItem(SUPERBUS_AUTH_TOKEN_STORAGE_KEY);
};

export const cleanImpersonateStorageKey = (): void => {
	if (!localStorage) return;
	localStorage.removeItem(storageImpersonateKey);
};

export const isStorageSupported = ((): boolean => {
	if (!localStorage) return false;

	try {
		localStorage.setItem(storageDefaultKey, Date.now().toString());
		localStorage.removeItem(storageDefaultKey);
		cleanDeprecatedStorageKey();
	} catch {
		return false;
	}

	return true;
})();

function get<T>(key: string): T extends undefined ? undefined : T | undefined;
function get<T>(
	key: string,
	defaultValue: T,
): T extends string
	? string
	: T extends number
		? number
		: T extends boolean
			? boolean
			: T extends undefined
				? unknown
				: T;
function get<T>(key: string, defaultValue?: T) {
	if (!isStorageSupported) {
		return defaultValue;
	}

	if (!storage.isset(key)) {
		return defaultValue;
	}

	const item = localStorage?.getItem(storage.formatKey(key));
	if (!item) return defaultValue;

	return JSON.parse(item);
}

const storage = {
	get,

	set: <T>(key: string, value: T): boolean => {
		if (!isStorageSupported || !localStorage) {
			return false;
		}

		if (!value) {
			localStorage.removeItem(key);
			return false;
		}

		localStorage.setItem(storage.formatKey(key), JSON.stringify(value));

		return true;
	},

	remove: (key: string): void => {
		if (!isStorageSupported || !localStorage) {
			return;
		}

		localStorage.removeItem(storage.formatKey(key));
	},

	isset: (key: string): boolean => {
		if (!isStorageSupported) {
			return false;
		}

		key = storage.formatKey(key);

		return Boolean(
			typeof localStorage?.[key] !== 'undefined' && localStorage[key] !== null,
		);
	},

	clear: (): void => {
		if (!isStorageSupported) {
			return;
		}

		localStorage?.clear();
	},

	formatKey: (key: string): string => `${key}`,
};

export default storage;
